import { useMemo } from 'react';
import { TechnicianInfo } from '../../../entities/user';
import {
  CalendarOrderList,
  CalendarOrderRow,
  CalendarOrderToolbar,
  useCalendarRows,
  type CalendarOrderEditor,
} from '../../../features/technician-calendar-order';
import { TECHNICIAN_COL_WIDTH, WEEK_DAY_COUNT } from '../../../shared/lib/schedule';
import { BOARD_LAYER } from '../model/boardLayers';
import type { DispositionWeek } from '../model/useDispositionWeek';
import { technicianTrades } from '../model/technicianTrades';
import { orderByTrade } from '../model/tradeMatch';
import { useGewerkLabels } from '../model/useGewerkLabels';
import { useDispositionWeekBoard } from '../model/useDispositionWeekBoard';
import { WeekCell } from './WeekCell';
import { WeekDayHeader } from './WeekDayHeader';

/**
 * The schedule payload carries no roles, and every technician on this board is a Handwerker —
 * which is exactly what `getPositionsFromRoles` renders for `WORKER`. Same reasoning as `BoardRow`.
 */
const TECHNICIAN_ROLES = ['WORKER'] as const;

/** Narrow enough that five columns plus the technician column fit a laptop without scrolling. */
const MIN_DAY_COLUMN_WIDTH = 180;

interface Props {
  week: DispositionWeek;
  /** Same meaning as on the day board: matching Handwerker first, the rest greyed out. */
  matchGewerkId?: number | null;
  /**
   * Planen or Umplanen is going on (a mode, or a docked form). The week then orders by what fits the
   * Auftrag, exactly like the day board — the Leitung's order and hidden flag are not consulted.
   */
  isPlanning: boolean;
  /** The Leitung's edit mode for the default order; see `DispositionBoardWidget`. */
  orderEditor: CalendarOrderEditor;
}

/**
 * The week board — technicians down the left, Montag … Freitag across the top.
 *
 * **Read-only by construction.** There is no time axis, so there is nothing to drag along and none
 * of the day board's geometry, drag primitive or planning machinery is reachable from here. A click
 * anywhere takes the user to the day board, which is where a block can actually be moved.
 *
 * Plain CSS grid rather than the day board's absolutely-positioned blocks: with no axis, a row's
 * height is simply its busiest cell, which grid gives for free.
 */
export const DispositionWeekWidget = ({
  week,
  matchGewerkId = null,
  isPlanning,
  orderEditor,
}: Props) => {
  const { rows, isLoading, isError, refetch } = useDispositionWeekBoard(
    week.days,
    week.from,
    week.to,
    week.todayIso,
  );

  const gewerkLabels = useGewerkLabels();
  const calendarRows = useCalendarRows(rows, orderEditor);
  const editingOrder = orderEditor.isEditing && !isPlanning;

  const orderedRows = useMemo(
    () =>
      isPlanning
        ? orderByTrade(
            [...rows].sort((a, b) => a.technicianId - b.technicianId),
            matchGewerkId,
          )
        : calendarRows.ordered,
    [isPlanning, rows, matchGewerkId, calendarRows.ordered],
  );

  const gridTemplateColumns = `${TECHNICIAN_COL_WIDTH}px repeat(${WEEK_DAY_COUNT}, minmax(${MIN_DAY_COLUMN_WIDTH}px, 1fr))`;

  // Every row is its own grid with the same columns, so a row can be picked up as one element.
  const gridStyle = {
    gridTemplateColumns,
    // Below this the columns would squeeze an address down to one word, so the grid scrolls
    // sideways instead; above it the `1fr` columns share the extra width.
    minWidth: TECHNICIAN_COL_WIDTH + WEEK_DAY_COUNT * MIN_DAY_COLUMN_WIDTH,
  };

  // A click on a cell opens the day; while the Leitung arranges the rows it must not.
  const openDay = editingOrder ? () => {} : week.openDay;

  return (
    <div className="h-full overflow-auto bg-white">
      {/*
        Header — the row itself is sticky, not its cells: a sticky cell cannot leave its own grid, and
        a grid of one row would scroll away with it. The corner stays sticky in x as well.
      */}
      <div className={`sticky top-0 grid ${BOARD_LAYER.header}`} style={gridStyle}>
        <div
          className={`sticky left-0 flex shrink-0 items-center justify-between gap-2 bg-white px-3 py-3 text-base text-zinc-500 outline outline-1 outline-offset-[-0.50px] outline-slate-200 ${BOARD_LAYER.headerCorner}`}
        >
          Handwerkern
          <CalendarOrderToolbar editor={orderEditor} disabled={isPlanning} />
        </div>
        {week.days.map((dayIso) => (
          <WeekDayHeader
            key={dayIso}
            dayIso={dayIso}
            isToday={dayIso === week.todayIso}
            onOpenDay={openDay}
          />
        ))}
      </div>

      <CalendarOrderList
        ids={orderedRows.map(({ row }) => row.technicianId)}
        editing={editingOrder}
        onMove={calendarRows.onMove}
      >
        {orderedRows.map(({ row, muted }) => (
          <CalendarOrderRow
            key={row.technicianId}
            technicianId={row.technicianId}
            editing={editingOrder}
            hidden={muted}
            onToggleHidden={calendarRows.onToggleHidden}
            className="grid"
            style={gridStyle}
          >
            {(controls) => (
              <>
                <div className={`sticky left-0 flex bg-white ${BOARD_LAYER.technicianColumn}`}>
                  <TechnicianInfo
                    technicianId={String(row.technicianId)}
                    technicianName={row.technicianName}
                    technicianRoles={[...TECHNICIAN_ROLES]}
                    technicianAvatar={row.technicianAvatar}
                    muted={muted}
                    trades={technicianTrades(row.trades, gewerkLabels, matchGewerkId)}
                    actions={controls}
                  />
                </div>

                {row.cells.map((cell) => (
                  <WeekCell
                    key={cell.dayIso}
                    cell={cell}
                    technicianId={row.technicianId}
                    onOpenDay={openDay}
                    muted={muted}
                  />
                ))}
              </>
            )}
          </CalendarOrderRow>
        ))}
      </CalendarOrderList>

      {isLoading && <WeekSkeleton />}

      {!isLoading && isError && (
        <WeekMessage
          text="Der Wochenplan konnte nicht geladen werden."
          action={{ label: 'Erneut versuchen', onClick: refetch }}
        />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <WeekMessage text="Keine Handwerker für diese Woche." />
      )}
    </div>
  );
};

const SKELETON_ROWS = 6;

/** Placeholder rows while the schedule query is in flight — one per expected technician. */
const WeekSkeleton = () => (
  <div className="animate-pulse">
    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
      <div key={i} className="flex h-[92px] border-b border-slate-200">
        <div
          className="flex shrink-0 items-center gap-3.5 border-r border-slate-200 p-3"
          style={{ width: TECHNICIAN_COL_WIDTH }}
        >
          <div className="h-11 w-11 shrink-0 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-28 rounded bg-gray-200" />
            <div className="h-3 w-20 rounded bg-gray-100" />
          </div>
        </div>
        <div className="flex-1" />
      </div>
    ))}
  </div>
);

const WeekMessage = ({
  text,
  action,
}: {
  text: string;
  action?: { label: string; onClick: () => void };
}) => (
  <div className="flex flex-col items-center gap-3 py-16 text-sm text-gray-400">
    <span>{text}</span>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="rounded border border-slate-200 px-3 py-1.5 text-[#124093] hover:bg-gray-50"
      >
        {action.label}
      </button>
    )}
  </div>
);
